import React, { useState } from 'react';

interface Category {
  id: string;
  name: string;
  type: string;
  icon?: string;
  color?: string;
  parent_id?: string;
  count?: number;
}

interface CategoryFilterProps {
  categories: Category[];
  selectedCategory: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  onCreateCategory?: () => void;
  loading?: boolean;
  totalCount?: number;
  uncategorizedCount?: number;
}

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories,
  selectedCategory,
  onSelectCategory,
  onCreateCategory,
  loading = false,
  totalCount,
  uncategorizedCount,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Group categories by parent
  const rootCategories = categories.filter(c => !c.parent_id);
  const childCategories = categories.filter(c => c.parent_id);

  const getChildCategories = (parentId: string) => {
    return childCategories.filter(c => c.parent_id === parentId);
  };

  const renderCategory = (category: Category, level: number = 0) => {
    const isSelected = selectedCategory === category.id;
    const children = getChildCategories(category.id);
    const hasChildren = children.length > 0;

    return (
      <div key={category.id} style={{ marginLeft: `${level * 16}px` }} className="animate-slide-in">
        <button
          onClick={() => onSelectCategory(isSelected ? null : category.id)}
          className={`category-item ${isSelected ? 'selected' : ''}`}
          style={{
            borderLeft: category.color ? `3px solid ${category.color}` : undefined,
          }}
        >
          <span className="category-icon">{category.icon || '📁'}</span>
          <span className="category-name">{category.name}</span>
          {category.count !== undefined && (
            <span className="category-count">{category.count}</span>
          )}
        </button>
        {hasChildren && children.map(child => renderCategory(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="category-filter">
      <div className="category-header">
        <button
          className="category-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
          <span className="gradient-text category-title">分类</span>
        </button>
        {onCreateCategory && (
          <button
            className="category-create-btn"
            onClick={onCreateCategory}
            title="创建新分类"
          >
            <span>+</span>
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="category-list">
          {loading ? (
            <div className="category-loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              <button
                onClick={() => onSelectCategory(null)}
                className={`category-item special-item ${selectedCategory === null ? 'selected' : ''}`}
              >
                <span className="category-icon">📋</span>
                <span className="category-name">全部</span>
                <span className="category-count">
                  {totalCount !== undefined ? totalCount : categories.reduce((sum, c) => sum + (c.count || 0), 0)}
                </span>
              </button>
              <button
                onClick={() => onSelectCategory('uncategorized')}
                className={`category-item special-item ${selectedCategory === 'uncategorized' ? 'selected' : ''}`}
              >
                <span className="category-icon">❓</span>
                <span className="category-name">未分类</span>
                <span className="category-count">
                  {uncategorizedCount || 0}
                </span>
              </button>
              <div className="category-divider"></div>
              {rootCategories.map(category => renderCategory(category))}
            </>
          )}
        </div>
      )}

      <style>{`
        .category-filter {
          background: var(--color-bg-primary);
          border-radius: var(--radius-lg);
          padding: var(--spacing-lg);
          box-shadow: var(--shadow-md);
          transition: all var(--transition-base);
        }

        .category-filter:hover {
          box-shadow: var(--shadow-lg);
        }

        .category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--color-gray-200);
        }

        .category-toggle {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          background: none;
          border: none;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          padding: var(--spacing-xs);
          color: var(--color-text-primary);
          transition: all var(--transition-base);
        }

        .category-toggle:hover .toggle-icon {
          transform: scale(1.2);
        }

        .toggle-icon {
          transition: transform var(--transition-base);
          font-size: 12px;
        }

        .category-title {
          font-size: 1.1rem;
          font-weight: 700;
        }

        .category-create-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px dashed var(--color-gray-300);
          background: var(--color-bg-primary);
          font-size: 20px;
          cursor: pointer;
          color: var(--color-gray-500);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all var(--transition-base);
        }

        .category-create-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
          background: var(--color-primary-light);
          transform: rotate(90deg);
        }

        .category-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .category-divider {
          height: 1px;
          background: linear-gradient(to right, transparent, var(--color-gray-200), transparent);
          margin: var(--spacing-sm) 0;
        }

        .category-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          border: none;
          background: var(--color-bg-primary);
          border-radius: var(--radius-md);
          cursor: pointer;
          text-align: left;
          width: 100%;
          transition: all var(--transition-base);
          position: relative;
          overflow: hidden;
        }

        .category-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 3px;
          background: var(--color-primary);
          transform: scaleX(0);
          transition: transform var(--transition-base);
        }

        .category-item:hover {
          background: var(--color-gray-50);
          transform: translateX(4px);
        }

        .category-item:hover::before {
          transform: scaleX(1);
        }

        .category-item.selected {
          background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-secondary-light) 100%);
          color: var(--color-primary);
          font-weight: 600;
        }

        .category-item.selected::before {
          transform: scaleX(1);
        }

        .category-item.special-item .category-icon {
          font-size: 20px;
        }

        .category-icon {
          font-size: 18px;
          flex-shrink: 0;
          transition: transform var(--transition-base);
        }

        .category-item:hover .category-icon {
          transform: scale(1.15);
        }

        .category-name {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 0.9rem;
        }

        .category-count {
          font-size: 12px;
          font-weight: 600;
          color: var(--color-text-tertiary);
          background: var(--color-gray-100);
          padding: 3px 10px;
          border-radius: var(--radius-full);
          min-width: 28px;
          text-align: center;
          transition: all var(--transition-base);
        }

        .category-item:hover .category-count {
          background: var(--color-gray-200);
        }

        .category-item.selected .category-count {
          background: var(--color-primary);
          color: white;
        }

        .category-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: var(--spacing-xl);
        }

        @media (max-width: 768px) {
          .category-filter {
            padding: var(--spacing-md);
          }

          .category-item {
            padding: var(--spacing-sm);
          }

          .category-name {
            font-size: 0.85rem;
          }
        }
      `}</style>
    </div>
  );
};
